import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import {
  ArrowLeft, Loader2, Plus, Tag, Trash2, Power, PowerOff, RefreshCw,
  Infinity as InfinityIcon, X as XIcon, Check,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPromosPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [promos, setPromos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(freshForm());
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate('/');
  }, [user, loading, navigate]);

  const fetchPromos = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/promos`);
      setPromos(data);
    } catch (e) {
      toast({ title: 'Failed to load promo codes', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  useEffect(() => { if (user?.is_admin) fetchPromos(); /* eslint-disable-next-line */ }, [user]);

  const createPromo = async () => {
    if (!form.code.trim()) { toast({ title: 'Code required' }); return; }
    if (!form.discount_pct || form.discount_pct <= 0 || form.discount_pct > 100) {
      toast({ title: 'Discount must be 1-100%' }); return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/promos`, {
        code: form.code.trim().toUpperCase(),
        discount_pct: Number(form.discount_pct),
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        first_order_only: !!form.first_order_only,
        once_per_user: !!form.once_per_user,
        active: !!form.active,
        description: form.description.trim(),
      });
      toast({ title: `Promo ${form.code.toUpperCase()} created` });
      setShowCreate(false); setForm(freshForm()); fetchPromos();
    } catch (e) {
      toast({ title: 'Create failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setCreating(false); }
  };

  const toggleActive = async (code) => {
    try {
      await axios.patch(`${API}/admin/promos/${code}/toggle`);
      fetchPromos();
    } catch (e) { toast({ title: 'Toggle failed', variant: 'destructive' }); }
  };

  const deletePromo = async (code) => {
    if (!window.confirm(`Delete promo code "${code}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/promos/${code}`);
      toast({ title: `Deleted ${code}` });
      fetchPromos();
    } catch (e) { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  if (loading || !user) return <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>;
  if (!user.is_admin) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <Link to="/admin" className="text-xs text-[#8247E5] hover:underline inline-flex items-center gap-1 mb-2" data-testid="admin-promos-back">
            <ArrowLeft className="w-3 h-3" /> Back to admin
          </Link>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2 dark:text-white">
            <Tag className="w-6 h-6 text-[#8247E5]" /> Promo codes
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Create, disable, or delete promo codes. Discounts apply to the service fee only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchPromos} disabled={busy} className="gap-2" data-testid="admin-promos-refresh">
            <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={() => setShowCreate(true)} className="gap-2 bg-[#8247E5] hover:bg-[#6d33cc]" data-testid="admin-promos-create-btn">
            <Plus className="w-4 h-4" /> New promo code
          </Button>
        </div>
      </div>

      {/* Promo table */}
      <div className="rounded-2xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-neutral-900/50 overflow-hidden">
        <div className="grid grid-cols-[1.5fr_0.6fr_1fr_1.4fr_0.8fr_auto] gap-3 px-5 py-3 border-b border-neutral-200 dark:border-white/10 text-[10px] uppercase tracking-widest font-bold text-neutral-500 dark:text-neutral-400">
          <div>Code</div>
          <div>Discount</div>
          <div>Usage</div>
          <div>Rules</div>
          <div>Status</div>
          <div className="text-right pr-1">Actions</div>
        </div>
        {promos.length === 0 ? (
          <div className="p-10 text-center text-sm text-neutral-500 dark:text-neutral-400">
            {busy ? 'Loading…' : 'No promo codes yet. Click "New promo code" to create one.'}
          </div>
        ) : (
          promos.map(p => <PromoRow key={p.code} promo={p} onToggle={toggleActive} onDelete={deletePromo} />)
        )}
      </div>

      {showCreate && (
        <CreateModal
          form={form} setForm={setForm}
          onClose={() => { setShowCreate(false); setForm(freshForm()); }}
          onSubmit={createPromo}
          submitting={creating}
        />
      )}
    </div>
  );
};

const PromoRow = ({ promo, onToggle, onDelete }) => {
  const cap = promo.max_uses;
  const used = promo.used_count || 0;
  const isCapped = cap != null;
  const pct = isCapped ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  const isFull = isCapped && used >= cap;
  return (
    <div className="grid grid-cols-[1.5fr_0.6fr_1fr_1.4fr_0.8fr_auto] gap-3 px-5 py-4 items-center border-b border-neutral-100 dark:border-white/5 last:border-0 hover:bg-neutral-50 dark:hover:bg-white/[0.02]" data-testid={`promo-row-${promo.code}`}>
      <div>
        <div className="font-mono font-bold text-neutral-900 dark:text-white text-base tracking-wider">{promo.code}</div>
        {promo.description && <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 line-clamp-1">{promo.description}</div>}
      </div>
      <div className="text-lg font-bold text-[#8247E5] dark:text-[#C084FC]">{promo.discount_pct}%</div>
      <div>
        <div className="text-sm font-semibold text-neutral-900 dark:text-white flex items-center gap-1">
          {used}
          <span className="text-neutral-400">/</span>
          {isCapped ? cap : <InfinityIcon className="w-3.5 h-3.5 text-neutral-400" />}
        </div>
        {isCapped && (
          <div className="mt-1 h-1 rounded-full bg-neutral-200 dark:bg-white/10 overflow-hidden">
            <div className={`h-full ${isFull ? 'bg-rose-500' : 'bg-gradient-to-r from-violet-500 to-fuchsia-500'}`} style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-1">
        {promo.first_order_only && <span className="text-[10px] font-semibold text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-500/15 px-2 py-0.5 rounded-full">First order only</span>}
        {promo.once_per_user && <span className="text-[10px] font-semibold text-sky-700 dark:text-sky-300 bg-sky-100 dark:bg-sky-500/15 px-2 py-0.5 rounded-full">Once per user</span>}
        {!promo.first_order_only && !promo.once_per_user && <span className="text-[10px] text-neutral-400">No restrictions</span>}
      </div>
      <div>
        {promo.active ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-500/15 px-2 py-0.5 rounded-full uppercase tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Active
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-white/10 px-2 py-0.5 rounded-full uppercase tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full bg-neutral-400" /> Paused
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 justify-end">
        <button
          onClick={() => onToggle(promo.code)}
          className="h-9 w-9 rounded-lg hover:bg-neutral-100 dark:hover:bg-white/10 flex items-center justify-center text-neutral-500 dark:text-neutral-400 hover:text-[#8247E5]"
          title={promo.active ? 'Pause' : 'Activate'}
          data-testid={`promo-toggle-${promo.code}`}
        >
          {promo.active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
        </button>
        <button
          onClick={() => onDelete(promo.code)}
          className="h-9 w-9 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-500/10 flex items-center justify-center text-neutral-500 hover:text-rose-600 dark:text-neutral-400 dark:hover:text-rose-400"
          title="Delete"
          data-testid={`promo-delete-${promo.code}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const CreateModal = ({ form, setForm, onClose, onSubmit, submitting }) => (
  <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white dark:bg-neutral-900 rounded-2xl max-w-lg w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="promo-create-modal">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-xl font-bold text-neutral-900 dark:text-white">New promo code</h2>
        <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-neutral-100 dark:hover:bg-white/10 flex items-center justify-center text-neutral-500"><XIcon className="w-4 h-4" /></button>
      </div>
      <div className="space-y-4">
        <div>
          <label className="text-xs font-semibold text-neutral-600 dark:text-neutral-400 mb-1 block">Code</label>
          <Input
            data-testid="new-promo-code"
            value={form.code}
            onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
            placeholder="e.g. TG20"
            className="font-mono tracking-wider h-11"
            maxLength={24}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-neutral-600 dark:text-neutral-400 mb-1 block">Discount (%)</label>
            <Input
              data-testid="new-promo-discount"
              type="number" min="1" max="100" step="0.5"
              value={form.discount_pct}
              onChange={e => setForm(f => ({ ...f, discount_pct: e.target.value }))}
              className="h-11"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600 dark:text-neutral-400 mb-1 block">Max uses (blank = unlimited)</label>
            <Input
              data-testid="new-promo-maxuses"
              type="number" min="1"
              value={form.max_uses}
              onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))}
              placeholder="unlimited"
              className="h-11"
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-neutral-600 dark:text-neutral-400 mb-1 block">Description (shown to users at checkout)</label>
          <Input
            data-testid="new-promo-desc"
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="e.g. 20% off service fee, spring sale"
            className="h-11"
          />
        </div>
        <div className="space-y-2 pt-1">
          <CheckOption
            label="First-order only"
            help="User must have zero paid/delivered orders"
            checked={form.first_order_only}
            onChange={v => setForm(f => ({ ...f, first_order_only: v }))}
            testid="new-promo-firstorder"
          />
          <CheckOption
            label="Once per user"
            help="Same email can't use this code twice"
            checked={form.once_per_user}
            onChange={v => setForm(f => ({ ...f, once_per_user: v }))}
            testid="new-promo-onceperuser"
          />
          <CheckOption
            label="Active immediately"
            help="Uncheck to create paused; you can toggle later"
            checked={form.active}
            onChange={v => setForm(f => ({ ...f, active: v }))}
            testid="new-promo-active"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={onSubmit} disabled={submitting} className="bg-[#8247E5] hover:bg-[#6d33cc] gap-2" data-testid="new-promo-submit">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Create
        </Button>
      </div>
    </div>
  </div>
);

const CheckOption = ({ label, help, checked, onChange, testid }) => (
  <label className="flex items-start gap-3 p-3 rounded-xl border border-neutral-200 dark:border-white/10 hover:border-violet-300 dark:hover:border-violet-500/40 cursor-pointer transition-colors" data-testid={testid}>
    <input
      type="checkbox"
      checked={checked}
      onChange={e => onChange(e.target.checked)}
      className="mt-0.5 w-4 h-4 rounded accent-[#8247E5]"
    />
    <div>
      <div className="text-sm font-semibold text-neutral-900 dark:text-white">{label}</div>
      <div className="text-xs text-neutral-500 dark:text-neutral-400">{help}</div>
    </div>
  </label>
);

const freshForm = () => ({
  code: '',
  discount_pct: '',
  max_uses: '',
  first_order_only: false,
  once_per_user: true,
  active: true,
  description: '',
});

export default AdminPromosPage;
